/**
 * Servicio WebSocket para pantallas de TV del menú
 * Migración de endpoints HTTP a WebSocket para actualizaciones en tiempo real
 */

const normalizeServerUrl = (url) => {
    if (!url) return url;
    if (typeof window !== 'undefined' && window.location.protocol === 'https:' && url.startsWith('http://')) {
        return url.replace('http://', 'https://');
    }
    return url;
};

class MenuWebSocketClient {
    constructor(shopBranchId, serverUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8001') {
        this.shopBranchId = shopBranchId;
        this.serverUrl = normalizeServerUrl(serverUrl);
        this.stompClient = null;
        this.isConnected = false;
        this.reconnectAttempts = 0;
        this.maxReconnectAttempts = 5;
        this.reconnectDelay = 5000;
        
        // Callbacks para manejar actualizaciones
        this.onFlavorCategoriesUpdate = null;
        this.onFlavorsUpdate = null;
        this.onIceCreamProductsUpdate = null;
        this.onCompleteMenuUpdate = null;
        this.onConnectionChange = null;
        this.onError = null;
    }

    async connect() {
        return new Promise((resolve, reject) => {
            try {
                // Verificar que las librerías WebSocket estén disponibles
                if (typeof window.SockJS === 'undefined' || typeof window.Stomp === 'undefined') {
                    throw new Error('WebSocket libraries (SockJS, Stomp) not loaded. Please include them in your HTML.');
                }

                const socket = new window.SockJS(`${this.serverUrl}/ws`);
                this.stompClient = window.Stomp.over(socket);
                
                // Deshabilitar logs de debug en producción
                this.stompClient.debug = process.env.NODE_ENV === 'development' ? console.log : null;

                this.stompClient.connect({}, (frame) => {
                    console.log('✅ Conectado al WebSocket:', frame);
                    this.isConnected = true;
                    this.reconnectAttempts = 0;
                    
                    if (this.onConnectionChange) {
                        this.onConnectionChange(true);
                    }
                    
                    this.setupSubscriptions();
                    this.requestInitialData();
                    resolve();
                }, (error) => {
                    console.error('❌ Error de conexión WebSocket:', error);
                    this.isConnected = false;
                    
                    if (this.onConnectionChange) {
                        this.onConnectionChange(false);
                    }
                    
                    if (this.onError) {
                        this.onError(error);
                    }
                    
                    this.handleReconnection();
                    reject(error);
                });
            } catch (error) {
                console.error('❌ Error inicializando WebSocket:', error);
                if (this.onError) {
                    this.onError(error);
                }
                reject(error);
            }
        });
    }

    disconnect() {
        if (this.stompClient && this.isConnected) {
            this.stompClient.disconnect();
            this.isConnected = false;
            console.log('🔌 Desconectado del WebSocket');
            
            if (this.onConnectionChange) {
                this.onConnectionChange(false);
            }
        }
    }

    setupSubscriptions() {
        if (!this.stompClient || !this.isConnected) return;

        try {
            // 1. Categorías de gustos
            this.stompClient.subscribe(`/topic/flavor-categories/${this.shopBranchId}`, (response) => {
                try {
                    const data = JSON.parse(response.body);
                    console.log('📂 Categorías actualizadas:', data);
                    if (this.onFlavorCategoriesUpdate) {
                        this.onFlavorCategoriesUpdate(data);
                    }
                } catch (error) {
                    console.error('Error procesando categorías:', error);
                }
            });

            // 2. Gustos
            this.stompClient.subscribe(`/topic/flavors/${this.shopBranchId}`, (response) => {
                try {
                    const data = JSON.parse(response.body);
                    console.log('🍦 Gustos actualizados:', data);
                    if (this.onFlavorsUpdate) {
                        this.onFlavorsUpdate(data);
                    }
                } catch (error) {
                    console.error('Error procesando gustos:', error);
                }
            });

            // 3. Productos de helados
            this.stompClient.subscribe(`/topic/products/ice-cream/${this.shopBranchId}`, (response) => {
                try {
                    const data = JSON.parse(response.body);
                    console.log('🧊 Productos de helados actualizados:', data);
                    if (this.onIceCreamProductsUpdate) {
                        this.onIceCreamProductsUpdate(data);
                    }
                } catch (error) {
                    console.error('Error procesando productos de helados:', error);
                }
            });

            // 4. Menú completo
            this.stompClient.subscribe(`/topic/menu/complete/${this.shopBranchId}`, (response) => {
                try {
                    const data = JSON.parse(response.body);
                    console.log('📋 Menú completo actualizado:', data);
                    if (this.onCompleteMenuUpdate) {
                        this.onCompleteMenuUpdate(data);
                    }
                } catch (error) {
                    console.error('Error procesando menú completo:', error);
                }
            });

            console.log('✅ Suscripciones WebSocket configuradas');
        } catch (error) {
            console.error('❌ Error configurando suscripciones:', error);
            if (this.onError) {
                this.onError(error);
            }
        }
    }

    requestInitialData() {
        if (!this.isConnected || !this.stompClient) return;

        try {
            // Solicitar datos iniciales del menú completo
            this.stompClient.send("/app/menu/complete", {}, JSON.stringify({
                shopBranchId: this.shopBranchId
            }));
            console.log('📤 Solicitando datos iniciales del menú');
        } catch (error) {
            console.error('❌ Error solicitando datos iniciales:', error);
            if (this.onError) {
                this.onError(error);
            }
        }
    }

    // Métodos para solicitar datos específicos
    requestFlavorCategories() {
        if (this.isConnected && this.stompClient) {
            try {
                this.stompClient.send("/app/menu/flavor-categories", {}, JSON.stringify({
                    shopBranchId: this.shopBranchId
                }));
                console.log('📤 Solicitando categorías de gustos');
            } catch (error) {
                console.error('❌ Error solicitando categorías:', error);
            }
        }
    }

    requestFlavors() {
        if (this.isConnected && this.stompClient) {
            try {
                this.stompClient.send("/app/menu/flavors", {}, JSON.stringify({
                    shopBranchId: this.shopBranchId
                }));
                console.log('📤 Solicitando gustos');
            } catch (error) {
                console.error('❌ Error solicitando gustos:', error);
            }
        }
    }

    requestFlavorsByCategory(categoryId) {
        if (this.isConnected && this.stompClient) {
            try {
                this.stompClient.send("/app/menu/flavors/category", {}, JSON.stringify({
                    categoryId: categoryId,
                    shopBranchId: this.shopBranchId
                }));
                console.log('📤 Solicitando gustos por categoría:', categoryId);
            } catch (error) {
                console.error('❌ Error solicitando gustos por categoría:', error);
            }
        }
    }

    requestIceCreamProducts() {
        if (this.isConnected && this.stompClient) {
            try {
                this.stompClient.send("/app/menu/products/ice-cream", {}, JSON.stringify({
                    shopBranchId: this.shopBranchId
                }));
                console.log('📤 Solicitando productos de helados');
            } catch (error) {
                console.error('❌ Error solicitando productos de helados:', error);
            }
        }
    }

    handleReconnection() {
        if (this.reconnectAttempts < this.maxReconnectAttempts) {
            this.reconnectAttempts++;
            console.log(`🔄 Intento de reconexión ${this.reconnectAttempts}/${this.maxReconnectAttempts}`);
            
            setTimeout(() => {
                this.connect()
                    .then(() => {
                        console.log('✅ Reconexión exitosa');
                    })
                    .catch((error) => {
                        console.error('❌ Error en reconexión:', error);
                    });
            }, this.reconnectDelay);
        } else {
            console.error('💥 Máximo número de intentos de reconexión alcanzado');
            if (this.onError) {
                this.onError(new Error('Máximo número de intentos de reconexión alcanzado'));
            }
        }
    }

    // Método para verificar el estado de la conexión
    getConnectionStatus() {
        return {
            isConnected: this.isConnected,
            reconnectAttempts: this.reconnectAttempts,
            maxReconnectAttempts: this.maxReconnectAttempts
        };
    }
}

export default MenuWebSocketClient;
